// The composer's voice notes (MacComposerControls' microphone + MacVoiceRecordingStrip): the mic
// sits beside send while the box is EMPTY and holds no files — "a half-written message and a voice
// note are alternatives, not companions" — and while a take records, the strip REPLACES the field
// and its tools: dot, clock, "Grabando…"/"En pausa", pause/resume, discard, send.
//
// Sending is the apps' sendVoiceNote: a message with no words and the one file. The bytes go
// through the attachments upload (tray.uploadForSend) and the core send carries the upload's local
// id, so the note appears at once and the worker posts it when the upload lands.
import { useEffect, useRef, type ReactNode } from 'react';
import { AttachmentKind } from '../../../core/attachmentKind';
import type { SendExtras } from '../../../bridge/protocol';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { uploadForSend } from '../attachments/tray';
import { voicePlayback } from './playback';
import { useVoiceRecorder, voiceRecorder } from './recorder';
import { micErrorKey } from './voiceFormat';
import { PauseGlyph, PlayGlyph } from './VoiceNoteCard';
import './voice.css';

export interface VoiceComposer {
  /** True while THIS composer's take is starting/recording/paused: the strip replaces the field. */
  recording: boolean;
  /** The strip (render inside `.composer`). */
  strip: ReactNode;
  /** A refusal sentence (render above the box). */
  notice: ReactNode;
  /** The microphone, beside send; null when the box holds text or files. */
  micButton(show: boolean): ReactNode;
}

export function useVoiceComposer(opts: { channelKey: string; channelId: string; disabled: boolean; onSend(message: string, extras: SendExtras): void }): VoiceComposer {
  const { channelKey, channelId, disabled } = opts;
  const state = useVoiceRecorder();
  const onSend = useRef(opts.onSend);
  onSend.current = opts.onSend;
  const mine = state.owner === channelKey && state.phase !== 'idle';

  // Leaving the conversation (or the composer going away) throws its take away, as the Mac's
  // per-conversation recorder does.
  useEffect(() => {
    return () => {
      if (voiceRecorder.snapshot.owner === channelKey) voiceRecorder.cancel();
      voiceRecorder.dismissError(channelKey);
    };
  }, [channelKey]);

  const start = () => {
    if (disabled) return;
    // A note playing would be recorded into the take.
    voicePlayback.stop();
    void voiceRecorder.start(channelKey);
  };

  const send = async () => {
    const take = await voiceRecorder.finish();
    if (!take) return;
    console.info('[satori-voice] sending', take.file.name, take.file.type, `${take.file.size} B`, `${take.duration.toFixed(1)} s`);
    const localId = uploadForSend(channelId, take.file);
    onSend.current('', { fileIds: [localId] });
  };

  const error = state.error?.owner === channelKey ? state.error.kind : null;
  const notice = error ? (
    <div className="voice-notice" role="alert">
      <Icon name="alertCircle" size={14} />
      <span>{t(micErrorKey[error])}</span>
      <button className="voice-notice-close" onClick={() => voiceRecorder.dismissError(channelKey)} title={t('common.close')} aria-label={t('common.close')}>
        <Icon name="x" size={13} />
      </button>
    </div>
  ) : null;

  const paused = state.phase === 'paused';
  const strip = mine ? (
    <div className={`voice-strip${paused ? ' paused' : ''}`} role="group" aria-label={t('channel.voiceNote')}>
      <span className="voice-dot" aria-hidden />
      <span className="voice-strip-clock">{AttachmentKind.clock(state.elapsed)}</span>
      <span className="voice-strip-label">{paused ? t('channel.recordingPaused') : t('channel.recording')}</span>
      <LevelMeter active={state.phase === 'recording'} />
      <span className="voice-strip-spacer" />
      <button
        className="voice-strip-pause"
        disabled={state.phase === 'starting'}
        onClick={() => voiceRecorder.togglePause()}
        aria-label={paused ? t('channel.voiceResume') : t('channel.voicePause')}
        title={paused ? t('channel.voiceResume') : t('channel.voicePause')}
      >
        {paused ? <PlayGlyph size={13} /> : <PauseGlyph size={13} />}
      </button>
      <button className="voice-strip-discard" onClick={() => voiceRecorder.cancel()} aria-label={t('web.voice.discard')} title={t('web.voice.discard')}>
        <Icon name="x" size={16} />
      </button>
      <button className="voice-strip-send" disabled={state.phase === 'starting'} onClick={() => void send()} aria-label={t('web.voice.send')} title={t('web.voice.send')}>
        <svg width={15} height={15} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 19.5V5M5.5 11 12 4.5 18.5 11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  ) : null;

  const busyElsewhere = state.phase !== 'idle' && state.owner !== channelKey;
  return {
    recording: mine,
    strip,
    notice,
    micButton: (show) =>
      show && !disabled ? (
        <button
          className="icon-button voice-mic"
          onMouseDown={(e) => e.preventDefault()}
          onClick={start}
          disabled={busyElsewhere}
          title={t('web.voice.record')}
          aria-label={t('web.voice.record')}
        >
          <Icon name="mic" size={17} />
        </button>
      ) : null,
  };
}

/**
 * A live input meter beside the clock — not in the apps (their OS shows the mic is live); in a
 * browser it is the one proof the RIGHT microphone is hearing you. Bars scroll left like a
 * waveform; drawn by direct style writes, never a React render per frame.
 */
function LevelMeter({ active }: { active: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const bars = Array.from(el.children) as HTMLElement[];
    const levels = new Array(bars.length).fill(0);
    let frame = 0;
    let last = 0;
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      if (now - last < 70) return;
      last = now;
      levels.shift();
      levels.push(active ? voiceRecorder.level() : 0);
      for (let i = 0; i < bars.length; i++) bars[i].style.transform = `scaleY(${Math.max(0.12, levels[i]).toFixed(3)})`;
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [active]);
  return (
    <span className="voice-meter" ref={ref} role="img" aria-label={t('web.voice.level')}>
      {Array.from({ length: 18 }, (_, i) => (
        <i key={i} />
      ))}
    </span>
  );
}
